import { useState, useEffect } from 'react';
import { useOptionalNetworkStatusContext } from '@/shared/providers/NetworkStatusProvider';
import { logger } from '@/shared/utils/logger';

export interface NetworkStatus {
  isOnline: boolean;
  wasOffline: boolean;
}

const useStandaloneNetworkStatus = (enabled: boolean): NetworkStatus => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [wasOffline, setWasOffline] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    let resetTimeout: ReturnType<typeof setTimeout> | undefined;

    const handleOnline = () => {
      logger.info('Network: Back online');
      setIsOnline(true);
      setWasOffline(true);

      // Reset wasOffline after a brief delay (for toast display)
      resetTimeout = setTimeout(() => {
        setWasOffline(false);
      }, 4000);
    };

    const handleOffline = () => {
      logger.info('Network: Gone offline');
      setIsOnline(false);
      setWasOffline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      if (resetTimeout) {
        clearTimeout(resetTimeout);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [enabled]);

  return { isOnline, wasOffline };
};

/**
 * Hook to monitor network connectivity status.
 * Uses the app-level provider when present and falls back to a local listener
 * for isolated tests or standalone component renders.
 */
export const useNetworkStatus = (): NetworkStatus => {
  const providerStatus = useOptionalNetworkStatusContext();
  const standaloneStatus = useStandaloneNetworkStatus(!providerStatus);

  return providerStatus ?? standaloneStatus;
};
